import React, {
  MouseEvent,
} from 'react';
import {
  Row,
} from '@tanstack/react-table';
import isEmpty from 'lodash/isEmpty';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import Name from 'pages/Dashboard/components/Name';
import {
  stringifyReportParameters,
} from 'utils/string';
import {
  formatDateTime,
} from 'utils/date';
import {
  ReportDTO,
} from 'dtos/reportDTO';
import {
  useConfirmDialog,
  useNotify,
  useSidePanel,
} from 'core/hooks';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';
import Tooltip from 'core/components/Tooltip';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from 'core/icons/delete';
import {
  useDeleteReport,
} from 'pages/Dashboard/pages/Reports/hooks';
import {
  reportEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import type {
  ReportState,
  RunOutputSource,
} from 'views/EMR/components/Panel/RunOutputPanel/types';
import RunStatusChip from 'views/EMR/components/Panel/RunOutputPanel/RunStatusChip';
import {
  RunActionsCell,
  RunSummaryLine,
} from 'views/EMR/components/Panel/RunOutputPanel/tableCells';
import {
  isRunPanelOpen,
} from 'views/EMR/components/Panel/RunOutputPanel/utils';

type CreatedByCellProps = {
  row: Row<ReportDTO>;
};

function CreatedByCell({
  row: { original: { requestedByUserDTO } },
}: CreatedByCellProps) {
  const src = useUserProfilePicturePath(requestedByUserDTO?.userId);

  return (
    <Name
      name={getDisplayName(requestedByUserDTO?.formattedNames)}
      image={{ src }}
    />
  );
}

export {
  readableReportState,
} from 'views/EMR/components/Panel/RunOutputPanel/types';
export type {
  ReportState,
} from 'views/EMR/components/Panel/RunOutputPanel/types';

export const fileStatusColumn: ColumnDef<ReportDTO> = {
  id: 'reportState',
  accessorKey: 'reportState',
  columnTitle: 'Status',
  cell: ({ row: { original: { reportState } } }) => (
    <RunStatusChip state={reportState as ReportState} />
  ),
};

type DeleteReportButtonProps = {
  source: RunOutputSource;
  reportId?: number;
};

function DeleteReportButton({ source, reportId }: DeleteReportButtonProps) {
  const notify = useNotify();
  const { mutateAsync: deleteReport } = useDeleteReport();
  const { confirm, closeConfirm } = useConfirmDialog();
  const { activePanel, closePanel } = useSidePanel();

  const handleDelete = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    confirm({
      content: 'Are you sure you want to delete this report?',
      onConfirm: async () => {
        await deleteReport({ reportId });
        if (isRunPanelOpen(activePanel, source)) {
          closePanel();
        }
        closeConfirm();
        reportEventChannel.emit('onReportChange');
        notify({
          message: 'Report deleted successfully.',
          variant: 'success',
        });
      },
      title: 'Delete Report',
      confirmText: 'Confirm',
    });
  };

  return (
    <Tooltip title="Delete Report">
      <IconButton
        aria-label="Delete Report"
        className="!w-8 !h-8 hover:!bg-gray-100"
        onClick={handleDelete}
      >
        <DeleteIcon className="!w-5 !h-5 text-gray-700" />
      </IconButton>
    </Tooltip>
  );
}

type ReportRunActionsCellProps = {
  row: Row<ReportDTO>;
};

function ReportRunActionsCell({ row }: ReportRunActionsCellProps) {
  const {
    reportId,
    reportState,
    fileCount,
  } = row.original;
  const source: RunOutputSource = { kind: 'report', reportId: reportId ?? 0 };

  return (
    <RunActionsCell
      source={source}
      state={reportState as ReportState}
      fileCount={fileCount ?? 0}
      extraActions={(
        <DeleteReportButton
          source={source}
          reportId={reportId}
        />
      )}
    />
  );
}

export const columns: ColumnDef<ReportDTO>[] = [

  fileStatusColumn,
  {
    id: 'reportDisplayName',
    accessorKey: 'reportDisplayName',
    columnTitle: 'Type / Parameters',
    meta: { autoSized: true },
    cell: ({
      row: {
        original: {
          reportDisplayName,
          parameters,
          reportState,
          fileCount,
          exceptionCount,
        },
      },
    }) => (
      <div>
        <div className="font-semibold">
          {reportDisplayName}
        </div>
        <RunSummaryLine
          state={reportState as ReportState}
          fileCount={fileCount ?? 0}
          exceptionCount={exceptionCount ?? 0}
          prefix={isEmpty(parameters) ? null : stringifyReportParameters(parameters as Record<string, any>)}
        />
      </div>
    ),
  }, {
    id: 'createdAt',
    accessorKey: 'createdAt',
    columnTitle: 'Created on',
    size: 100,
    cell: ({ row: { original: { createdOn } } }) => (
      formatDateTime(createdOn ?? '')
    ),
  }, {
    id: 'createdBy',
    accessorKey: 'createdBy',
    columnTitle: 'Created by',
    size: 250,
    cell: ({ row }) => <CreatedByCell row={row} />,
  }, {
    id: 'actions',
    header: '',
    size: 96,
    cell: ({ row }) => <ReportRunActionsCell row={row} />,
  }];
